import { Node } from '@tiptap/react'
import { tokenLabel } from '@/features/messaging/tokens'

/**
 * A personalisation token ({{first_name}}…) inside the rich-text editor, shown as a chip in words
 * ("Client’s first name") and removed as one piece — instead of literal {{code}} the agent could half
 * delete. It serialises to the very same text, `<span data-token="first_name">{{first_name}}</span>`,
 * so what's saved and sent is unchanged: the backend sanitizer keeps the {{…}} text and fills it in.
 */
export const TokenNode = Node.create({
  name: 'token',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return { name: { default: '' } }
  },

  parseHTML() {
    return [{ tag: 'span[data-token]', getAttrs: (el) => ({ name: (el as HTMLElement).getAttribute('data-token') ?? '' }) }]
  },

  renderHTML({ node }) {
    return ['span', { 'data-token': node.attrs.name }, `{{${node.attrs.name}}}`]
  },

  renderText({ node }) {
    return `{{${node.attrs.name}}}`
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('span')
      dom.className = 'pp-token'
      dom.contentEditable = 'false'
      dom.textContent = tokenLabel(`{{${node.attrs.name}}}`)
      dom.title = `Filled in for each person: {{${node.attrs.name}}}`
      return { dom }
    }
  },
})

// Names may carry a "contact." / "deal." prefix (custom fields) and digits.
const TOKEN_SPAN = /<span data-token="([a-z0-9_.]+)">\{\{[a-z0-9_.]+\}\}<\/span>/g
const RAW_TOKEN = /\{\{\s*([a-z0-9_.]+)\s*\}\}/g

/**
 * Turn every {{token}} in stored HTML (a template, an old draft) into the editor's token markup, so it
 * loads as a chip. Idempotent: already-wrapped tokens are unwrapped first.
 */
export function tokensToChips(html: string): string {
  return html.replace(TOKEN_SPAN, '{{$1}}').replace(RAW_TOKEN, '<span data-token="$1">{{$1}}</span>')
}
